import { useState } from "react";
import { Edit3Icon, EyeIcon, RotateCcw, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds } from "../../../Components/Utils";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchBlogsQuery,
  useFetchTrashedBlogsQuery,
  useDeleteBlogMutation,
  useDestroyBlogMutation,
  useRestoreBlogMutation,
  useBulkDeleteBlogsMutation,
  useBulkDestroyBlogsMutation,
  useBulkRestoreBlogsMutation,
} from "../../../stores/api/blogs.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { blog_image_url } from "../../../Components/ImageUrls";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "image", label: "Image", sortable: false, type: "image", imageBaseUrl: blog_image_url },
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  { key: "category.name", label: "Category" },
  { key: "createdAt", label: "Date" , sortable:false},
  {
    key: "blog_status",
    label: "Status",
    type: "badge",
    badgeColors: {
      Published: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-white-300",
      Draft: "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300",
      Active: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-white-300",
      Inactive: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-400",
    },
  },
];

const BlogList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchBlogsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedBlogsQuery(queryParams, { skip: !trashed });
  const { data: blogs = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteBlog] = useDeleteBlogMutation();
  const [destroyBlog] = useDestroyBlogMutation();
  const [restoreBlog] = useRestoreBlogMutation();
  const [bulkDeleteBlogs] = useBulkDeleteBlogsMutation();
  const [bulkDestroyBlogs] = useBulkDestroyBlogsMutation();
  const [bulkRestoreBlogs] = useBulkRestoreBlogsMutation();

  const resetPage = () => setPage(1);
  const rows = blogs.data.map((r) => ({
    ...r,
    blog_status: r.status === '1' ? "Published": (r.status === '2' ? "Archive" : "Draft") ,
    createdAt: new Date(r.createdAt).toLocaleString("en-PK", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
  }));
  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Blog`,
      message: `${verb} "${row.title}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreBlog(row.id).unwrap();
          else if (type === "destroy") await destroyBlog(row.id).unwrap();
          else await deleteBlog(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `Blog ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = getSelectedIds(selectedItems);
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} blog(s)?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") await bulkRestoreBlogs(body).unwrap();
          else if (type === "destroy") await bulkDestroyBlogs(body).unwrap();
          else await bulkDeleteBlogs(body).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} blog(s) ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Blogs" />
      <TableListing
        headers={HEADERS}
        paginationData={{...blogs,data: rows}}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.CREATE_BLOG}
        handleBulk={handleBulk}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <div className="flex gap-2">
            {!trashed ? (
             <> <Button
                onClick={() => navigate(ROUTE_PATHS.CREATE_BLOG, { state: { blog: row } })}
                title="Edit"
                icon={Edit3Icon}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              /> <Button
                onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
                title="View"
                icon={EyeIcon}
                className="text-purple-600 hover:bg-purple-100 hover:text-purple-700 dark:hover:bg-purple-500/10"
              /></>
            ) : (
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
                
            )}
           
            <Button
              onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />
    </>
  );
};

export default BlogList;
